/**
 * thankYouRoute.ts — where checkout ENDS, as a constant, plus the hand-over it
 * carries.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: thank-you route +
 * post-payment scroll reset (2026-08-20).
 *
 * WHY THIS ROUTE EXISTS AT ALL (CTO, relaying marketing): they *"need a separate
 * thank you page after the payment to track conversions with URL change"*.
 * Until now the confirmation was a COMPONENT SWAP inside `/:clinicSlug` —
 * `stage === 'confirm'` rendered `OrderConfirmation` and the address bar never
 * moved. A GTM History Change trigger has nothing to fire on and a GA4
 * destination goal has no destination, so the single most valuable moment in the
 * funnel was invisible to the container.
 *
 * ── THE PATH CARRIES NOTHING, AND THAT IS THE HARD RULE ────────────────────
 * No order id, no order reference, no assessment id, no medication, no price —
 * not in the path, not in a query string. This URL is read by EVERY tag in a
 * container administered outside this estate and lands in GA4 as
 * `page_location`, where it is retained, joined and exported. A "harmless"
 * `?order=` would put an order identifier — and, with it, the fact that a named
 * person bought a weight-management treatment — into an ad-tech pipeline that
 * has no lawful basis to hold it (UK-GDPR Art. 9; the Part F guardrails in
 * `tracking/dataLayer.ts` exist for exactly this). A static path cannot leak.
 *
 * ── WHY A TOP-LEVEL SEGMENT AND NOT `/:clinicSlug/thank-you` ───────────────
 * `tracking/gtm.ts`'s `isTrackedPath` is an ALLOW-LIST and it decides whether
 * GTM loads at all. A two-segment path is only tracked when the second segment
 * is `callback`; `/weightloss/thank-you` therefore takes the "anything deeper is
 * NOT an acquisition surface" branch, returns false, and marketing would get
 * NOTHING on the one page they asked for. This path is a SINGLE segment, so it
 * takes the `/:clinicSlug` journey branch and is tracked with no change to that
 * fail-closed function. React Router scores static segments above dynamic ones,
 * so it can never be mistaken for a clinic slug by the router either.
 *
 * That single-segment branch is the one place `isTrackedPath` fails OPEN, and
 * `gtm.ts` requires every new top-level route to be considered there in the same
 * commit. It has been: this route is DELIBERATELY not reserved. It is the end of
 * the acquisition journey, the surface the container exists to measure, and it
 * carries nothing identifying. `tracking/gtm.spec.ts` pins that decision so a
 * later tidy-up of `RESERVED_SEGMENTS` cannot switch marketing off by accident.
 */
import type { PaymentCaptureRoute } from './paymentCopy';

/** The confirmation page. Static, clean, and never parameterised — see above. */
export const THANK_YOU_PATH = '/thank-you';

/**
 * What checkout hands the confirmation page, in react-router navigation state.
 *
 * IN THE HISTORY ENTRY, NEVER THE URL. `location.state` is stored in the
 * browser's own history entry: it survives a reload of this page (so a patient
 * who refreshes still sees their confirmation instead of being stranded), it is
 * never sent to a server, and it never reaches a tag. The medication name has to
 * travel somehow — the confirmation says it back to the patient — and the
 * history entry is the only channel here that is neither the URL nor storage we
 * would then have to expire.
 *
 * IT AUTHORISES NOTHING. Nothing on the confirmation page reads or writes an
 * order; this state only chooses copy. Anyone can forge a history entry on their
 * own browser and look at a confirmation for an order that does not exist — they
 * would be fooling nobody but themselves, because no order was created and no
 * money moved. Never grow this into something a server decision depends on.
 */
export type ThankYouNavState = {
  /** Present ⇒ checkout handed us a real completed order. Absent ⇒ show nothing. */
  orderPlaced: true;
  /**
   * True only when the patient has JUST finished checkout on this navigation —
   * false when we are re-showing a confirmation for an order that was completed
   * earlier (a hard refresh mid-journey resumes to a finished OrderCase). It is
   * what stops the front-end conversion event counting the same checkout twice.
   */
  justCompleted: boolean;
  /** Hold vs immediate capture — server-authoritative, from `paymentPhase`. */
  captureRoute: PaymentCaptureRoute;
  /** Shown back to the patient in the confirmation line. Never tracked. */
  medicationName?: string;
  /** The OPAQUE acquisition lead id — the funnel join key, never an identifier. */
  leadId?: string;
  /**
   * True when the patient already had the post-payment celebration in checkout
   * (the modal a guest sees on the account step, CTO 2026-09-24), so this page
   * does not throw a second burst of confetti after they create their account.
   * Presentation only, like everything here: it chooses whether decoration
   * plays and nothing else.
   */
  celebrated?: boolean;
};

/**
 * Read the hand-over defensively, or `null` when there isn't one.
 *
 * DEFENSIVE BECAUSE THE INPUT IS NOT OURS. History state survives a reload, is
 * restored by the browser from disk, and can be written by anything running on
 * this origin. It is `unknown` at the type level for a reason, and an unchecked
 * cast here would let a restored-from-disk blob put a non-string into the
 * confirmation copy — a render crash on the page a patient reaches seconds after
 * paying. A shape we do not recognise is treated as "no order in hand", which is
 * a screen we already have to render properly anyway.
 */
export function readThankYouNavState(state: unknown): ThankYouNavState | null {
  if (typeof state !== 'object' || state === null) return null;
  const raw = state as Record<string, unknown>;
  if (raw.orderPlaced !== true) return null;
  return {
    orderPlaced: true,
    justCompleted: raw.justCompleted === true,
    // Anything other than the explicit 'immediate' falls back to 'hold', which is
    // the copy that says the card has NOT been charged. If this value is ever
    // wrong, over-stating a charge is the harmful direction — so the safe
    // fallback is the one that promises less.
    captureRoute: raw.captureRoute === 'immediate' ? 'immediate' : 'hold',
    medicationName: typeof raw.medicationName === 'string' && raw.medicationName ? raw.medicationName : undefined,
    leadId: typeof raw.leadId === 'string' && raw.leadId ? raw.leadId : undefined,
    // Only an explicit `true` suppresses the confetti; anything unrecognised
    // means "not celebrated yet", whose worst case is one more burst of
    // decoration.
    celebrated: raw.celebrated === true,
  };
}
